import { z } from 'zod'

import { diasCorridos, periodoStatus } from '../utils/periodo'
import {
  FERIAS_STATUS,
  type Ferias,
  type FeriasPayload,
  type FeriasStatus,
  type FeriasUpdatePayload,
} from './ferias'

export const FERIAS_MIN_DIAS = 5
export const FERIAS_MAX_DIAS = 30
export const FERIAS_MAX_ABONO = 10
export const FERIAS_OBSERVACAO_MAX = 500

const isIsoDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value)

/**
 * Espelha as regras simples da API. As que dependem do histórico (saldo do período, máximo de
 * 3 partes, sobra de 1 a 4 dias, parte de 14 dias) só a API valida — o erro volta no campo.
 */
export const feriasFormSchema = z
  .object({
    dataInicioGozo: z.string().refine(isIsoDate, 'Informe o início do gozo'),
    dataFimGozo: z.string().refine(isIsoDate, 'Informe o fim do gozo'),
    diasAbonoPecuniario: z.string(),
    status: z.enum(FERIAS_STATUS),
    observacao: z
      .string()
      .trim()
      .max(FERIAS_OBSERVACAO_MAX, `A observação deve ter até ${FERIAS_OBSERVACAO_MAX} caracteres`),
  })
  .superRefine((values, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: 'custom', path: [path], message })

    const abono = Number(values.diasAbonoPecuniario)
    const abonoValido = Number.isInteger(abono) && abono >= 0 && abono <= FERIAS_MAX_ABONO
    if (!abonoValido) issue('diasAbonoPecuniario', `O abono vai de 0 a ${FERIAS_MAX_ABONO} dias`)

    const { dataInicioGozo: inicio, dataFimGozo: fim } = values
    if (!isIsoDate(inicio) || !isIsoDate(fim)) return

    const dias = diasCorridos(inicio, fim)
    if (dias < FERIAS_MIN_DIAS || dias > FERIAS_MAX_DIAS) {
      issue(
        'dataFimGozo',
        `O gozo deve ter de ${FERIAS_MIN_DIAS} a ${FERIAS_MAX_DIAS} dias corridos`,
      )
    } else if (abonoValido && dias + abono > FERIAS_MAX_DIAS) {
      issue(
        'diasAbonoPecuniario',
        `Gozo + abono não podem passar de ${FERIAS_MAX_DIAS} dias (hoje: ${dias + abono})`,
      )
    }
  })

export type FeriasFormValues = z.infer<typeof feriasFormSchema>

export const EMPTY_FERIAS_FORM: FeriasFormValues = {
  dataInicioGozo: '',
  dataFimGozo: '',
  diasAbonoPecuniario: '0',
  status: 'PENDENTE',
  observacao: '',
}

const toDateInput = (value: string | null) => value?.slice(0, 10) ?? ''

export function feriasToFormValues(ferias: Ferias): FeriasFormValues {
  return {
    dataInicioGozo: toDateInput(ferias.dataInicioGozo),
    dataFimGozo: toDateInput(ferias.dataFimGozo),
    diasAbonoPecuniario: String(ferias.diasAbonoPecuniario),
    status: ferias.status,
    observacao: ferias.observacao ?? '',
  }
}

export function toFeriasPayload(values: FeriasFormValues): FeriasPayload {
  return {
    dataInicioGozo: values.dataInicioGozo,
    dataFimGozo: values.dataFimGozo,
    diasAbonoPecuniario: Number(values.diasAbonoPecuniario),
    observacao: values.observacao.trim() || null,
  }
}

export function toFeriasUpdatePayload(values: FeriasFormValues): FeriasUpdatePayload {
  return { ...toFeriasPayload(values), status: values.status }
}

/**
 * Status aceitos pela API no PUT, conforme as datas do gozo em relação a `hoje`:
 * - ainda não começou: PENDENTE, APROVADA ou CANCELADA;
 * - em andamento: só EM_GOZO; já terminou: só CONCLUIDA;
 * - registro já cancelado não muda mais de status.
 * Serve para montar as opções da tela — a API continua validando.
 */
export function feriasStatusPermitidos(
  dataInicioGozo: string,
  dataFimGozo: string,
  statusAtual: FeriasStatus,
  hoje?: string,
): FeriasStatus[] {
  if (statusAtual === 'CANCELADA') return ['CANCELADA']
  const inicio = dataInicioGozo.slice(0, 10)
  const fim = dataFimGozo.slice(0, 10)
  if (!isIsoDate(inicio) || !isIsoDate(fim)) return [statusAtual]

  switch (periodoStatus(inicio, fim, hoje)) {
    case 'agendado':
      return ['PENDENTE', 'APROVADA', 'CANCELADA']
    case 'em-andamento':
      return ['EM_GOZO']
    case 'encerrado':
      return ['CONCLUIDA']
  }
}
